import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader } from '@lms/ui';
import { api } from '@/lib/api';
import { moduleIcon, type LibraryPickerItem, type LibraryPickerMeta } from './course-builder-types';

interface AddContentModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (learningObjectId: string) => Promise<void>;
  inserting?: boolean;
  libraryCreateHref?: string;
}

export function AddContentModal({ open, onClose, onSelect, inserting, libraryCreateHref = '/library/create' }: AddContentModalProps) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<LibraryPickerItem[]>([]);
  const [meta, setMeta] = useState<LibraryPickerMeta>({
    current_page: 1,
    last_page: 1,
    per_page: 5,
    total: 0,
  });

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setPage(1);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          search,
          page: String(page),
          per_page: '5',
        });
        const data = await api<{ data: LibraryPickerItem[]; meta: LibraryPickerMeta }>(
          `/library/picker?${params}`,
        );
        if (!cancelled) {
          setResults(data.data ?? []);
          setMeta(
            data.meta ?? { current_page: 1, last_page: 1, per_page: 5, total: data.data?.length ?? 0 },
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, search ? 300 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, search, page]);

  const pageNumbers = useMemo(() => {
    const last = Math.max(1, meta.last_page);
    const current = Math.max(1, meta.current_page);
    const windowSize = 5;
    let start = Math.max(1, current - Math.floor(windowSize / 2));
    const end = Math.min(last, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    const pages: number[] = [];
    for (let p = start; p <= end; p += 1) pages.push(p);
    return pages;
  }, [meta]);

  if (!open) return null;

  return (
    <div className="lms-add-content-modal" role="dialog" aria-modal="true" aria-labelledby="lms-add-content-modal-title">
      <div className="lms-add-content-modal__panel">
        <div className="lms-add-content-modal__header">
          <h2 id="lms-add-content-modal-title" className="lms-add-content-modal__title">
            Añadir una actividad o un recurso
          </h2>
          <button type="button" className="lms-add-content-modal__close" aria-label="Cerrar" onClick={onClose}>
            <span className="material-symbols-outlined" aria-hidden="true">
              close
            </span>
          </button>
        </div>

        <div className="lms-add-content-modal__body">
          <div className="lms-add-content-modal__search">
            <span className="material-symbols-outlined lms-add-content-modal__search-icon" aria-hidden="true">
              search
            </span>
            <input
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Buscar actividad o recurso..."
              className="lms-add-content-modal__search-input"
            />
          </div>

          <div className="lms-add-content-modal__list-wrap">
            {loading ? (
              <div className="lms-add-content-modal__loading">
                <Loader label="Cargando…" />
              </div>
            ) : results.length > 0 ? (
              <div className="lms-add-content-modal__list">
                {results.map((obj) => (
                  <button
                    key={obj.id}
                    type="button"
                    className="lms-add-content-modal__item group"
                    disabled={inserting}
                    onClick={() => onSelect(obj.id)}
                  >
                    <div className="lms-add-content-modal__item-main">
                      <div className="lms-add-content-modal__item-icon">
                        <span className="material-symbols-outlined" aria-hidden="true">
                          {moduleIcon(obj.typeValue)}
                        </span>
                      </div>
                      <div className="min-w-0 text-left">
                        <p className="lms-add-content-modal__item-title">{obj.name}</p>
                        <p className="lms-add-content-modal__item-type">{obj.type}</p>
                      </div>
                    </div>
                    <span className="material-symbols-outlined lms-add-content-modal__item-chevron" aria-hidden="true">
                      chevron_right
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="lms-add-content-modal__empty">No se encontraron objetos en la biblioteca.</p>
            )}
          </div>

          {meta.last_page > 1 && (
            <nav className="lms-add-content-modal__pager" aria-label="Paginación de biblioteca">
              <button
                type="button"
                className="lms-add-content-modal__pager-nav"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <span className="material-symbols-outlined text-[1.125rem]" aria-hidden="true">
                  chevron_left
                </span>
                Anterior
              </button>
              <div className="lms-add-content-modal__pager-pages">
                {pageNumbers.map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`lms-add-content-modal__pager-page${p === page ? ' lms-add-content-modal__pager-page--active' : ''}`}
                    disabled={loading}
                    aria-current={p === page ? 'page' : undefined}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="lms-add-content-modal__pager-nav lms-add-content-modal__pager-nav--next"
                disabled={page >= meta.last_page || loading}
                onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
              >
                Siguiente
                <span className="material-symbols-outlined text-[1.125rem]" aria-hidden="true">
                  chevron_right
                </span>
              </button>
            </nav>
          )}
        </div>

        <div className="lms-add-content-modal__footer">
          <Link to={libraryCreateHref} target="_blank" rel="noopener noreferrer" className="lms-add-content-modal__create">
            Crear actividad
          </Link>
        </div>
      </div>
    </div>
  );
}
